import { css } from "@compiled/react";
import { useIntl, FormattedMessage } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import UFOSegment from "@atlaskit/react-ufo/segment";
import { token } from "@atlaskit/tokens";

import { Picture } from "@/components/wpl/image/src";
import { Button, Link, Video } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import { DEFAULT_VALUES } from "@/libraries/web-framework";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import { CalendarIcon, MapDropIcon, Team26Logo } from "./illustrations";

// Mobile/small viewport breakpoint: below 1160px custom for this video and content to ensure it isn't cut off
const mediaBelow1160 = "@media (max-width: 1160px)";

// Background images for different viewport widths
const BG_IMAGES = {
    // Up to 1440px
    small: {
        left: "https://dam-cdn.atl.orangelogic.com/AssetLink/b25ni6jq86en824330eb181373404py3.svg",
        right: "https://dam-cdn.atl.orangelogic.com/AssetLink/72737qhytbe162wp8518852f85krd8jn.svg"
    },
    // Up to 1920px
    medium: {
        left: "https://dam-cdn.atl.orangelogic.com/AssetLink/w8asdprwnlr3ga7g2h43yi55c7lkkc0j.svg",
        right: "https://dam-cdn.atl.orangelogic.com/AssetLink/3n8vb435pvy77552h511lrvdg84f22tw.svg"
    },
    // 2560px and beyond
    large: {
        left: "https://dam-cdn.atl.orangelogic.com/AssetLink/7n8glrg7ika480x6aod11g33m84p0umk.svg",
        right: "https://dam-cdn.atl.orangelogic.com/AssetLink/k331kq8e41686dh833iahp6vj44x0283.svg"
    }
};

export type HeroTakeoverTeam26Props = {
    urlPath?: string;
};

export const HeroTakeoverTeam26 = () => {
    const intl = useIntl();

    const heroTakeoverTeam26Body = intl.formatMessage({
        id: "HomePage.hero-takeover-team-26.body",
        defaultMessage:
            "Join the global conference for AI-forward teams, their leaders, and the apps and agents that fuel them."
    });

    const heroTakeoverTeam26DateInfo = intl.formatMessage({
        id: "HomePage.hero-takeover-team-26.date-info",
        defaultMessage: "May 5 - 7"
    });

    const heroTakeoverTeam26LocationInfo = intl.formatMessage({
        id: "HomePage.hero-takeover-team-26.location-info",
        defaultMessage: "Anaheim, California and online"
    });

    const heroTakeoverTeam26CTASecondaryText =
        intl.formatMessage({
            id: "HomePage.hero-takeover-team-26.cta-secondary-text",
            defaultMessage: "See the agenda"
        }) || "See the agenda";

    const registerLink =
        "https://events.atlassian.com/team?utm_medium=event-web-referral&utm_source=cross-promo&utm_campaign=701qb00000tzh4byar&utm_content=K:event%7CH:in-person%7CN:team_26%7CP:agnostic%7CO:event-marketing%7CF:pre-event%7CC:event-home%7CQ:agnostic&event_context=wac-takeover-feb-2026&event_id=288640";
    const agendaLink =
        "https://events.atlassian.com/team/agenda?utm_medium=event-web-referral&utm_source=cross-promo&utm_campaign=701qb00000tzh4byar&utm_content=K:event%7CH:in-person%7CN:team_26%7CP:agnostic%7CO:event-marketing%7CF:pre-event%7CC:event-home%7CQ:agnostic&event_context=wac-takeover-feb-2026&event_id=288640";

    if (!checkFeatureFlagGate(featureFlags.gwp9191Team26HeroHomepageTakeover)) {
        return null;
    }

    return (
        <UFOSegment name="homepage-v2-hero-team-26">
            <div css={heroTeam26CSS}>
                <div css={sideDecoration}>
                    <div css={leftDecorationSmall}>
                        <Picture
                            image={{
                                src: BG_IMAGES.small.left,
                                alt: ""
                            }}
                            style={{ maxWidth: "none" }}
                        />
                    </div>
                    <div css={leftDecorationMedium}>
                        <Picture
                            image={{
                                src: BG_IMAGES.medium.left,
                                alt: ""
                            }}
                            style={{ maxWidth: "none" }}
                        />
                    </div>
                    <div css={leftDecorationLarge}>
                        <Picture
                            image={{
                                src: BG_IMAGES.large.left,
                                alt: ""
                            }}
                            style={{ maxWidth: "none" }}
                        />
                    </div>
                </div>
                <div css={containingWidth}>
                    <div css={leftHero}>
                        <div css={logoAndTimePlaceDetails}>
                            <Team26Logo />
                            <div css={timePlaceDetails}>
                                <div css={heroTakeoverTeam26BodyText}>
                                    <CalendarIcon />
                                    {heroTakeoverTeam26DateInfo}
                                </div>
                                <div css={heroTakeoverTeam26BodyText}>
                                    <MapDropIcon />
                                    {heroTakeoverTeam26LocationInfo}
                                </div>
                            </div>
                        </div>
                        <div>
                            <h2 className="hero hero-26">
                                <FormattedMessage
                                    id="HomePage.hero-takeover-team-26.heading"
                                    defaultMessage="Unlock human-AI collaboration at scale"
                                    values={DEFAULT_VALUES}
                                />
                            </h2>
                            <p css={heroTakeoverTeam26BodyText}>
                                {heroTakeoverTeam26Body}
                            </p>
                        </div>

                        <div css={buttonArea}>
                            <Button
                                variant="primary_alt"
                                href={registerLink}
                                size="large"
                                dataEventProps={{
                                    "data-event": "clicked",
                                    "data-event-component": "linkButton",
                                    // @ts-ignore
                                    "data-event-container":
                                        "heroTakeoverteam26",
                                    "data-product-key": "home",
                                    "data-label-english": "Register now"
                                }}
                            >
                                <FormattedMessage
                                    id="HomePage.hero-takeover-team-26.cta-primary-text"
                                    defaultMessage="Register now"
                                />
                            </Button>

                            <Link
                                text={heroTakeoverTeam26CTASecondaryText}
                                href={agendaLink}
                                type="primary"
                                bgColor="bgBlackBlue"
                                aria-label={heroTakeoverTeam26CTASecondaryText}
                                ariaLabel={heroTakeoverTeam26CTASecondaryText}
                                anchorProps={{
                                    "aria-label":
                                        heroTakeoverTeam26CTASecondaryText
                                }}
                                dataEventProps={{
                                    "data-event": "clicked",
                                    "data-event-component": "linkButton",
                                    // @ts-ignore
                                    "data-event-container":
                                        "heroTakeoverteam26",
                                    "data-product-key": "home",
                                    "data-label-english": "See the agenda"
                                }}
                            />
                        </div>
                    </div>
                    <div css={rightHero}>
                        <div css={videoWrapper}>
                            <Video
                                src="https://dam-cdn.atl.orangelogic.com/AssetLink/7i81vo662w3gc8s86hw21fe3314g7j50.mp4"
                                alt="Team 26 conference video"
                                loop={true}
                                disableAutoplay={false}
                                poster="https://dam-cdn.atl.orangelogic.com/AssetLink/42wp4t8o843x2uj0cc217d1ycn2r3ra3.svg"
                            />
                        </div>
                    </div>
                </div>
                <div css={sideDecorationRight}>
                    <div css={rightDecorationSmall}>
                        <Picture
                            image={{
                                src: BG_IMAGES.small.right,
                                alt: ""
                            }}
                            style={{
                                maxWidth: "none"
                            }}
                        />
                    </div>
                    <div css={rightDecorationMedium}>
                        <Picture
                            image={{
                                src: BG_IMAGES.medium.right,
                                alt: ""
                            }}
                            style={{
                                maxWidth: "none"
                            }}
                        />
                    </div>
                    <div css={rightDecorationLarge}>
                        <Picture
                            image={{
                                src: BG_IMAGES.large.right,
                                alt: ""
                            }}
                            style={{
                                maxWidth: "none"
                            }}
                        />
                    </div>
                </div>
            </div>
        </UFOSegment>
    );
};

const heroTeam26CSS = css({
    overflow: "hidden",
    margin: `0 0 ${token("space.600")}`,
    position: "relative",
    display: "flex",
    height: "368px",
    alignItems: "stretch",
    justifyContent: "center",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [mediaBelow1160]: {
        height: "auto",
        margin: `0 0 ${token("space.200")}`,
        padding: 0
    }
});

const sideDecoration = css({
    display: "flex",
    justifyContent: "flex-end",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [mediaBelow1160]: {
        display: "none"
    }
});

const sideDecorationRight = css({
    display: "flex",
    justifyContent: "flex-start",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [mediaBelow1160]: {
        display: "none"
    }
});

// Left decoration - small (up to 1440px)
const leftDecorationSmall = css({
    "height": "100%",
    "display": "block",
    "@media (min-width: 1441px)": {
        display: "none"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "img": {
        height: "100%",
        width: "auto",
        objectFit: "cover"
    }
});

// Left decoration - medium (1441px to 1920px)
const leftDecorationMedium = css({
    "height": "100%",
    "display": "none",
    "@media (min-width: 1441px)": {
        display: "block"
    },
    "@media (min-width: 1921px)": {
        display: "none"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "img": {
        height: "100%",
        width: "auto",
        objectFit: "cover"
    }
});

// Left decoration - large (1921px and beyond)
const leftDecorationLarge = css({
    "height": "100%",
    "display": "none",
    "@media (min-width: 1921px)": {
        display: "block"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "img": {
        height: "100%",
        width: "auto",
        objectFit: "cover"
    }
});

// Right decoration - small (up to 1440px)
const rightDecorationSmall = css({
    "height": "100%",
    "display": "block",
    "@media (min-width: 1441px)": {
        display: "none"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "img": {
        height: "100%",
        width: "auto",
        objectFit: "cover"
    }
});

// Right decoration - medium (1441px to 1920px)
const rightDecorationMedium = css({
    "height": "100%",
    "display": "none",
    "@media (min-width: 1441px)": {
        display: "block"
    },
    "@media (min-width: 1921px)": {
        display: "none"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "img": {
        height: "100%",
        width: "auto",
        objectFit: "cover"
    }
});

// Right decoration - large (1921px and beyond)
const rightDecorationLarge = css({
    "height": "100%",
    "display": "none",
    "@media (min-width: 1921px)": {
        display: "block"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "img": {
        height: "100%",
        width: "auto",
        objectFit: "cover"
    }
});

const containingWidth = css({
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: token("space.400"),
    // content is always 1068px wide, but there needs padding on the sides
    width: "1168px",
    // no tokens match this
    // eslint-disable-next-line
    background: "#101214",
    // more spacing on right side, less on left
    padding: `${token("space.400")} ${token("space.800")} ${token(
        "space.400"
    )} ${token("space.0")}`,
    // prevent small border gap from showing
    border: `1px solid #101214`,
    // make sure that the sliver of white doesn't show between elements
    marginInlineStart: "-1px",
    marginInlineEnd: "-1px",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [mediaBelow1160]: {
        width: "100%",
        padding: `${token("space.400")} ${token("space.400")}`,
        flexDirection: "column",
        textAlign: "center",
        alignItems: "center",
        gap: token("space.200"),
        justifyContent: "center"
    }
});

const leftHero = css({
    "position": "relative",
    "display": "flex",
    "gap": token("space.300"),
    "flexDirection": "column",
    "alignItems": "flex-start",
    "width": "568px",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "h2.hero": {
        "fontWeight": "600",
        "fontSize": "2.75rem",
        "margin": 0,
        "lineHeight": "1.2",
        "color": wacToken("color.neutral.light0"),
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "strong, span": {
            display: "block"
        },

        "&.hero-26": {
            fontWeight: "700",
            paddingBlockEnd: token("space.050"),
            [media.below.lg]: {
                fontSize: "32px"
            }
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "p": {
        color: wacToken("color.neutral.light0"),
        fontSize: "1.25rem",
        margin: 0,
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        [mediaBelow1160]: {
            fontSize: "1.125rem"
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [mediaBelow1160]: {
        width: "auto",
        padding: `0 ${token("space.400")} ${token("space.400")}`,
        textAlign: "center",
        alignItems: "center",
        justifyContent: "center"
    }
});

const rightHero = css({
    position: "relative",
    width: "468px",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [mediaBelow1160]: {
        width: "100vw"
    }
});

const videoWrapper = css({
    width: "100%",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    video: {
        border: 0,
        width: "468px",
        height: "298px",
        objectFit: "cover",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        [mediaBelow1160]: {
            maxWidth: "100%",
            width: "100%",
            height: "auto"
        }
    }
});

const timePlaceDetails = css({
    display: "flex",
    flexWrap: "wrap",
    gap: token("space.300"),
    fontSize: "1rem",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [mediaBelow1160]: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: token("space.100")
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    div: {
        color: wacToken("color.neutral.light0"),
        alignItems: "center",
        display: "flex",
        gap: token("space.100")
    }
});

const logoAndTimePlaceDetails = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.200"),
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [mediaBelow1160]: {
        gap: token("space.300")
    }
});

const heroTakeoverTeam26BodyText = css({
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "24px"
});

const buttonArea = css({
    "display": "flex",
    "flexWrap": "wrap",
    "gap": token("space.300"),
    "alignItems": "center",
    "justifyContent": "flex-start",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [mediaBelow1160]: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: token("space.400")
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".link-wrapper a": {
        "color": wacToken("color.neutral.light0"),
        ":visited": {
            color: wacToken("color.neutral.light0")
        },
        ":hover": {
            color: wacToken("color.extended.blue100")
        }
    },
    // override link arrow color
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".link-wrapper a svg path": {
        fill: wacToken("color.neutral.light0") // or your desired color
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".link-wrapper a:hover svg path": {
        fill: wacToken("color.neutral.light0") // hover color for arrow
    }
});

HeroTakeoverTeam26.displayName = "HeroTakeoverTeam26";
